import React, { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Box, TablePagination, ThemeProvider, createTheme } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MenuDropDown from './MenuDropdown';
import { GREEN } from '../../Assets/Constants/theme';
import DataGridIcon from '../../Assets/Images/DataGrid.svg';
import { ReactComponent as TableFilterIcon } from '../../Assets/Images/TableSortIcon.svg';

const CustomNoRowsOverlay = () => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%'
      }}
    >
      <img src={DataGridIcon} alt='No Data' style={{ height: '200px', widows: '200px' }} />
      <span>No Data</span>
    </div>
  );
};

const theme = createTheme({
  components: {
    MuiCircularProgress: {
      styleOverrides: {
        colorPrimary: {
          color: '#FFFFFF'
        }
      }
    }
  }
});

const InventoryTable = ({
  rows = [],
  columns = [],
  newMobileNumberMap,
  hasDuplicates,
  totalRowCount,
  updatePageCount = () => {},
  showLoader = false,
  rowsPerPageRender,
  onFilterChange,
  marginX,
  pageCount = 1,
  pageSize = 10,
  columnVisibilityModel,
  tableStatus = true,
  handleSortModelChange,
  filterSearch,
  apiRef,
  noPagination = false
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [columnData, setColumnData] = useState([]);
  const mapPageToNextCursor = React.useRef({});

  useEffect(() => {
    setIsLoading(showLoader);
  }, [showLoader]);

  const [paginationModel, setPaginationModel] = React.useState({
    page: pageCount - 1,
    pageSize: pageSize
  });

  useEffect(() => {
    setPaginationModel({
      page: pageCount - 1,
      pageSize: pageSize
    });
  }, [pageCount, pageSize]);

  useEffect(() => {
    if (!isLoading) {
      mapPageToNextCursor.current[paginationModel.page] = paginationModel.page + 1;
      updatePageCount(paginationModel.page + 1, paginationModel.pageSize);
    }
  }, [paginationModel.page, isLoading, paginationModel.pageSize]);

  const handlePaginationModelChange = (e, newPaginationModel) => {
    if (newPaginationModel === 0 || mapPageToNextCursor.current[newPaginationModel - 1]) {
      setPaginationModel(prev => ({ ...prev, page: newPaginationModel }));
    }
  };

  const handleChangeRowsPerPage = event => {
    setPaginationModel(prev => ({ ...prev, page: 0, pageSize: parseInt(event.target.value, 10) }));
  };

  useEffect(() => {
    if (columns && tableStatus) {
      let index = columns.findIndex(val => val.field == 'status');
      let temp = columns.map((val, i) => {
        if (i == index) {
          return {
            ...val,
            minWidth: val.minWidth ? val.minWidth : 130,
            renderCell: params => {
              return (
                <>
                  {params?.value?.toLowerCase() == 'available' ? (
                    <Box
                      sx={{
                        width: '50%',
                        minWidth: '70px',
                        height: '80%',
                        borderRadius: '8px',
                        color: '#228B22',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'start',
                        fontWeight: 500
                      }}
                    >
                      <span>{params?.value?.charAt(0)?.toUpperCase() + params?.value?.slice(1)}</span>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        minWidth: '70px',
                        height: '80%',
                        borderRadius: '8px',
                        color:
                          params?.value?.toLowerCase() == 'used'
                            ? '#4169E1'
                            : params?.value?.toLowerCase() == 'reserved' ||
                                params?.value?.toLowerCase() === 'in-progress'
                              ? '#FFAC1C'
                              : 'red',
                        display: 'flex',
                        alignItems: 'center',
                        fontWeight: 500,
                        justifyContent: 'start'
                      }}
                    >
                      <span>{params?.value?.charAt(0)?.toUpperCase() + params?.value?.slice(1)}</span>
                    </Box>
                  )}
                </>
              );
            },
            valueGetter: params => params?.row?.status
          };
        } else {
          return { ...val };
        }
      });
      setColumnData([...temp]);
    } else {
      setColumnData(columns);
    }
  }, [columns]);

  let newCustomCol = [...columnData];

  const temp = newCustomCol.map(item => {
    return {
      ...item,
      sortComparator: () => {
        return [];
      }
    };
  });

  newCustomCol = [...temp];

  return (
    <Box sx={{ width: '100%' }}>
      {hasDuplicates ? (
        <div>
          <h1>Duplicate Exists</h1>
          {Object.keys(newMobileNumberMap).map(mobileNumber => {
            const duplicateObjects = newMobileNumberMap[mobileNumber];
            if (duplicateObjects.length > 1) {
              return (
                <div key={mobileNumber}>
                  <h3>Mobile Number: {mobileNumber}</h3>
                  <ul>
                    {duplicateObjects.map((obj, index) => (
                      <li key={index}>{obj.userName}</li>
                    ))}
                  </ul>
                  Upload Again
                </div>
              );
            }
            return null;
          })}
        </div>
      ) : (
        <ThemeProvider theme={theme}>
          <DataGrid
            sx={{
              mx: marginX ? marginX : '20px',
              fontFamily: 'Gilroy-Medium',
              '& .MuiDataGrid-columnHeaders': { color: '#8E8E8E', fontSize: '12px' },

              '& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus-within': {
                outline: 'none'
              },

              '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': {
                outline: 'none'
              },
              '& .MuiDataGrid-cell': {
                border: 'none'
              },
              '.MuiDataGrid-iconButtonContainer': {
                visibility: 'visible',
                stroke: '#B7D423',
                width: 'auto'
              },
              '.MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeader--sorted) .MuiDataGrid-sortIcon ': {
                opacity: 1,
                stroke: '#646464'
              },
              '--DataGrid-overlayHeight': '300px',
              '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '8px' },
              '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '15px' },
              '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: '22px' },
              minHeight: !showLoader && rows.length < 5 ? '0px' : '500px'
            }}
            apiRef={apiRef}
            getRowHeight={() => 'auto'}
            columnVisibilityModel={columnVisibilityModel}
            rows={rows}
            columns={newCustomCol}
            disableColumnSelector
            filterMode='server'
            sortingMode='server'
            rowCount={+totalRowCount || rows?.length}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
              filter: {
                filterModel: { items: [{ field: filterSearch?.field, value: filterSearch?.value, operator: '=' }] }
              }
            }}
            localeText={{
              noResultsOverlayLabel: newCustomCol && newCustomCol[0] && newCustomCol[0]?.field === 'more' ? '' : ''
            }}
            pageSizeOptions={[10, 25, 50]}
            slots={{
              pagination: showLoader || noPagination ? null : TablePaginationDemo,
              noRowsOverlay: CustomNoRowsOverlay,
              columnSortedAscendingIcon: TableFilterIcon,
              columnSortedDescendingIcon: TableFilterIcon
            }}
            autoHeight={true}
            slotProps={{
              filterPanel: {
                filterFormProps: {
                  operatorInputProps: {
                    sx: { display: 'none' }
                  }
                }
              },
              pagination: {
                handleChangeRowsPerPage: handleChangeRowsPerPage,
                count: rows.length == 0 ? 0 : +totalRowCount || 10,
                rowsPerPage: paginationModel.pageSize,
                page: paginationModel.page,
                handleChangePage: handlePaginationModelChange
              }
            }}
            loading={showLoader}
            paginationModel={paginationModel}
            paginationMode={rowsPerPageRender ? 'server' : 'client'}
            onPaginationModelChange={handlePaginationModelChange}
            onFilterModelChange={onFilterChange}
            onSortModelChange={handleSortModelChange}
          />
        </ThemeProvider>
      )}
    </Box>
  );
};

function TablePaginationDemo({ handleChangeRowsPerPage, handleChangePage, page, rowsPerPage, count }) {
  return (
    <TablePagination
      component='div'
      count={count}
      page={page}
      onPageChange={handleChangePage}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={handleChangeRowsPerPage}
      rowsPerPageOptions={[10, 25, 50]}
    />
  );
}

export default InventoryTable;
